import React, { useMemo, useState } from 'react';
import { SwitchTransition, CSSTransition } from 'react-transition-group';
import { NextPage } from 'next';

import { logger } from '@belong/logging/logger';
import AnalyticsContext from '@belong/providers/analyticsEvents/Context';
import SectionCategoryFilter from '@belong/ui-sections/sections/sectionCategoryFilter/view';
import SectionPageTitle from '@belong/ui-sections/sections/sectionPageTitle/view';
import { ContentfulSection } from '@belong/ui-sections';
import { VisuallyHidden } from '@belong/ui-components';
import { withBasePath, withoutParams } from '@belong/url-utils';

import SectionLinkGroups from 'src/components/sections/SectionLinkGroups/view';
import {
  isPageError,
  ISupportPageData,
  ISupportServerSidePageProps,
  PageServerSidePropsFunction
} from 'src/core/types';
import { GLOBAL_SECTIONS } from 'src/models/sections';
import { withServerSideAppProps } from 'src/helpers/config/withServerSideAppProps';

import loadCategoryPageData from './CategoryPage.data';

const CategoryPage: NextPage<ISupportPageData> = ({ data }: ISupportPageData) => {
  const { category, parentCategory, rootCategory, articles, sections, settings, analytics } = data;
  const defaultOption = {
    label: sections[GLOBAL_SECTIONS.FILTER].options[0],
    value: ''
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const filterOptions = useMemo(() => [defaultOption, ...articles.map(({ label }) => label)], [articles]);
  const [filter, setFilter] = useState<string>(defaultOption.value);
  const showFilters = filterOptions.length > 2;
  const rootSlug = rootCategory?.slug;

  const defaultHelpSection = sections[GLOBAL_SECTIONS.GET_HELP];
  const categoryHelpSection =
    {
      mobile: sections[GLOBAL_SECTIONS.GET_HELP_MOBILE],
      internet: sections[GLOBAL_SECTIONS.GET_HELP_INTERNET]
    }[rootSlug] || defaultHelpSection;

  const activeGroupIdx = articles.findIndex(article => article.label === filter);
  const activeGroup = activeGroupIdx > -1 ? [articles[activeGroupIdx]] : articles;
  const articleCount = activeGroup.reduce((total, article) => total + article.items.length, 0);

  const onFilterChange = (newValue: string): void => {
    if (newValue !== filter) {
      setFilter(newValue);
    }
  };

  const transitionProps = {
    timeout: {
      enter: 200,
      exit: 200
    },
    key: filter,
    unmountOnExit: true,
    mountOnEnter: true,
    classNames: 'link-group'
  };

  const sectionPageTitleProps = {
    variant: 'Small',
    backgroundColor: 'BLACK',
    heading: category.pageTitle,
    id: category.id
  };

  return (
    <>
      <AnalyticsContext value={analytics} />
      <SectionPageTitle {...(sectionPageTitleProps as any)} />
      <VisuallyHidden role="status">{`${articleCount} article results.`}</VisuallyHidden>
      {showFilters && (
        <SectionCategoryFilter
          hasPaddingTop
          compressOnMobile
          {...sections[GLOBAL_SECTIONS.FILTER]}
          image={category.categoryImage}
          onChange={onFilterChange}
          options={filterOptions}
          selectedValue={filter}
        />
      )}
      <SwitchTransition mode="out-in">
        <CSSTransition {...transitionProps}>
          <SectionLinkGroups
            hasPaddingTop
            hasPaddingBottom
            hasBackground="white"
            showGroupTitles={articles.length > 1}
            articles={activeGroup}
            parentCategory={parentCategory}
            parentCategoryLinkLabel={settings.categoryPageParentLink}
          />
        </CSSTransition>
      </SwitchTransition>
      <ContentfulSection data-testid="section-get-help" {...categoryHelpSection} />
    </>
  );
};

export const getServerSidePageProps: PageServerSidePropsFunction = async (context, { config, host }) => {
  // actual page url without basePath
  const pageUrl = withoutParams(context.resolvedUrl);
  const { categories, sections, settings, mobileAlert, internetAlert } = config;
  const category = categories.getCategoryByPath(pageUrl);
  const rootCategory = categories.getRootCategory(pageUrl);
  const parentCategory = categories.getParentCategory(pageUrl);

  if (!category) {
    logger.error(`Could not find category for '${pageUrl}`);
    return { notFound: true };
  }

  const pageData = await loadCategoryPageData(categories, category);
  if (isPageError(pageData)) {
    logger.error(`Couldn't load category page data with category:${category}`);
    return { notFound: true };
  }

  const canonicalUrl = `https://${host}${withBasePath(category.path)}`;

  const alert = {
    mobile: mobileAlert,
    internet: internetAlert
  }[rootCategory.slug];

  const categoryPageProps: ISupportServerSidePageProps = {
    meta: {
      pageTitle: category.pageTitle,
      description: category.metaDescription,
      keywords: category.metaKeywords,
      canonicalUrl
    },
    data: {
      analytics: {
        flow: `${rootCategory.slug.toLowerCase()} support`,
        pageTitle: category.pageTitle
      },
      category,
      settings,
      parentCategory,
      rootCategory,
      sections,
      alert,
      ...pageData
    }
  };

  return categoryPageProps;
};
export const getServerSideProps = withServerSideAppProps(getServerSidePageProps);

export default CategoryPage;
